import { useState } from "react";
import { PieChart, History, Wallet } from "lucide-react";
import { FadeUp, RevealLine } from "./AnimatedSection";
import { useLanguage } from "@/context/LanguageContext";

const ICONS = [PieChart, History, Wallet];
const MIN_REVENUE = 200;
const MAX_REVENUE = 20000;
const formatUSD = (n) => `$${Math.round(n).toLocaleString("en-US")}`;

export const RoyaltyCard = () => {
  const { t } = useLanguage();
  const [revenue, setRevenue] = useState(2000);
  const artistShare = revenue * 0.9;
  const dobleRShare = revenue * 0.1;
  const percent = ((revenue - MIN_REVENUE) / (MAX_REVENUE - MIN_REVENUE)) * 100;

  return (
    <section id="regalias" data-testid="royalty-section" className="relative py-28 md:py-36 bg-[#0A0A0A] overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-brand-red/[0.10] via-transparent to-transparent animate-float" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-brand-blue/[0.12] via-transparent to-transparent animate-float-slow" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <p className="text-brand-red text-xs font-mono tracking-[0.25em] font-semibold uppercase mb-4">{t.royalty.chapterLabel}</p>
        <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight mb-14 max-w-2xl">
          <RevealLine text={t.royalty.title} />
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <FadeUp className="lg:col-span-3 rounded-3xl bg-black/40 backdrop-blur-2xl border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.5)] p-8 md:p-12 flex flex-col justify-between">
            <div>
              <span data-testid="royalty-badge" className="inline-flex items-center gap-3 rounded-full border border-white/15 bg-white/[0.04] px-2 py-2 mb-8">
                <span className="text-white font-heading font-bold text-sm bg-brand-red rounded-full px-4 py-1.5">{t.royalty.badgePart1}</span>
                <span className="text-white font-heading font-bold text-sm bg-brand-blue rounded-full px-4 py-1.5">{t.royalty.badgePart2}</span>
              </span>
              <p className="text-neutral-300 text-base md:text-lg leading-relaxed max-w-lg">{t.royalty.paragraph}</p>
            </div>

            <div className="mt-10 pt-8 border-t border-white/[0.08]">
              <div className="flex items-center justify-between mb-4">
                <p className="text-neutral-300 text-sm font-mono font-semibold uppercase tracking-wide">{t.royalty.calculatorLabel}</p>
                <span data-testid="royalty-calculator-revenue" className="font-mono text-white font-bold text-base">{formatUSD(revenue)}</span>
              </div>
              <input
                type="range"
                min={MIN_REVENUE}
                max={MAX_REVENUE}
                step={100}
                value={revenue}
                onChange={(e) => setRevenue(Number(e.target.value))}
                data-testid="royalty-calculator-slider"
                aria-label={t.royalty.calculatorRevenueLabel}
                className="w-full h-1.5 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-[0_0_0_5px_rgba(255,255,255,0.12)] [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer"
                style={{ background: `linear-gradient(to right, #2E5EFF 0%, #2E5EFF ${percent}%, rgba(255,255,255,0.1) ${percent}%, rgba(255,255,255,0.1) 100%)` }}
              />
              <p className="text-neutral-500 text-xs mt-2.5">{t.royalty.calculatorRevenueLabel}</p>

              <div className="flex items-end gap-3 mt-10 h-28">
                <div className="w-full h-[90%] bg-gradient-to-t from-brand-blueDark to-brand-blue rounded-t-md relative flex flex-col justify-end pb-2">
                  <span data-testid="royalty-calculator-artist-amount" className="absolute -top-9 left-0 right-0 text-center text-white font-heading font-black text-base leading-tight">
                    {formatUSD(artistShare)}
                  </span>
                  <span className="text-white text-[11px] font-bold text-center px-1">{t.royalty.calculatorArtistLabel}</span>
                </div>
                <div className="w-full h-[10%] bg-gradient-to-t from-brand-redDark to-brand-red rounded-t-md relative border border-white/10 flex flex-col justify-end">
                  <span data-testid="royalty-calculator-doubler-amount" className="absolute -top-9 left-0 right-0 text-center text-brand-red font-heading font-black text-base leading-tight">
                    {formatUSD(dobleRShare)}
                  </span>
                </div>
              </div>
              <p className="text-neutral-500 text-[11px] mt-2 text-right">{t.royalty.calculatorDobleRLabel}</p>
            </div>
          </FadeUp>

          <div className="lg:col-span-2 grid grid-rows-3 gap-6">
            {t.royalty.sideCards.map((item, i) => {
              const Icon = ICONS[i % ICONS.length];
              const accent = i % 2 === 0 ? "text-brand-blue" : "text-brand-red";
              return (
                <FadeUp key={item.title} delay={i * 0.1} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 flex items-start gap-4 hover:border-white/25 hover:-translate-y-0.5 transition-all duration-300">
                  <Icon className={`w-6 h-6 shrink-0 mt-1 ${accent}`} strokeWidth={1.5} />
                  <div>
                    <h4 className="font-heading font-bold text-white text-[15px] mb-1">{item.title}</h4>
                    <p className="text-neutral-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
